"use client";

import { useEffect, useState } from 'react';
import { loadBaselineIfEmpty, forceReloadBaseline, checkAndApplyUpdates } from '@/lib/localdb/baseline-initializer';
import { useToast } from '@/hooks/use-toast';
import { announceToScreenReader } from '@/lib/accessibility';

export default function BaselineInitializer() {
  const [isImporting, setIsImporting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Expose forceReloadBaseline to window for debugging
    if (typeof window !== 'undefined') {
      (window as any).checkAndApplyUpdates = async () => {
        if (process.env.NODE_ENV === 'development') console.log('Checking and applying baseline updates...');
        const res = await checkAndApplyUpdates();
        if (process.env.NODE_ENV === 'development') console.log('Baseline update check result:', res);
        if (res.updated) {
          toast({ title: 'Baseline Updated', description: res.reason || `Updated to version ${res.version}.` });
          window.location.reload();
        } else {
          toast({ title: 'No Baseline Changes', description: res.reason || 'Already up to date.' });
        }
        return res;
      };

      (window as any).forceReloadBaseline = async () => {
        if (process.env.NODE_ENV === 'development') console.log('Forcing baseline reload...');
        setIsImporting(true);
        const res = await forceReloadBaseline();
        if (process.env.NODE_ENV === 'development') console.log('Baseline reload result:', res);
        if (res.loaded) {
          toast({ title: 'Baseline Reloaded', description: 'Seed data has been re-imported.' });
          window.location.reload();
        } else {
          setIsImporting(false);
          toast({ title: 'Reload Failed', description: res.reason, variant: 'destructive' });
        }
        return res;
      };
    }

    let cancelled = false;

    (async () => {
      try {
        // 1. First run check
        const res = await loadBaselineIfEmpty();
        if (cancelled) return;

        if (res.loaded) {
          toast({ title: 'Loaded baseline data', description: 'First-run seed-data.json imported.' });
          announceToScreenReader('Baseline data loaded successfully');
        } else {
          // 2. If not first run, check for updates/backfills.
          const updateRes = await checkAndApplyUpdates();
          if (cancelled) return;

          if (updateRes.updated) {
            toast({
              title: 'Baseline Updated',
              description: updateRes.reason || `Updated to version ${updateRes.version}.`,
            });
          }
        }
      } catch (err) {
        console.error('Baseline initialization check error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  // Show a loading indicator only during manual baseline reload
  if (isImporting) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm font-medium text-muted-foreground">Loading baseline data…</p>
        </div>
      </div>
    );
  }

  return null;
}
